import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { finalize, forkJoin } from 'rxjs';
import { UserApiService } from 'src/app/data-transfer/services/user-api-service';
import { LicenseService } from 'src/app/services/licensing-service';
import { SnackbarService } from 'src/app/services/snackbar.service';
import { OrganizationDto } from 'src/app/data-transfer/entities/organization-dto';
import { CountryDto } from 'src/app/data-transfer/entities/country-dto';
import { CompanyDto } from 'src/app/data-transfer/entities/doc-entities/company-dto';
import { DocProfileDto } from 'src/app/data-transfer/entities/doc-entities/doc-profile-dto';
import { getApiErrorMessage } from '../admin-api-error';

type CompanyFormValue = { address: string | null; postCode: string | null; city: string | null; location: string | null };

@Component({
  selector: 'app-company-data',
  templateUrl: './company-data.component.html',
  styleUrls: ['./company-data.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompanyDataComponent implements OnInit {
  private readonly userApiService = inject(UserApiService);
  private readonly licenseService = inject(LicenseService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly translocoService = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly noDocProfileId = '';

  readonly organizationInput = input<OrganizationDto>();
  readonly allCountriesInput = input<CountryDto[]>();
  readonly docProfilesInput = input<DocProfileDto[]>([]);
  readonly saved = output<void>();

  protected readonly organization = signal(new OrganizationDto());
  protected readonly allCountries = signal<CountryDto[]>([]);
  protected readonly isPPWRAllowed = this.licenseService.isPPWRAllowed;
  protected readonly isSaving = signal(false);
  protected readonly expanded = signal(false);

  protected readonly organizationForm = new FormGroup({
    address: new FormControl<string | null>(null),
    postCode: new FormControl<string | null>(null),
    city: new FormControl<string | null>(null),
    location: new FormControl<string | null>(null),
  });

  private readonly formValue = toSignal(this.organizationForm.valueChanges, { initialValue: this.organizationForm.getRawValue() });
  protected readonly selfManufacturer = computed(() => this.organization().manufacturerProfiles?.find(profile => profile.companyProfileId === 0));
  protected readonly selfDocProfileId = signal(this.noDocProfileId);
  private readonly originalSnapshot = signal('');
  readonly hasChanges = computed(() => this.createSnapshot(this.formValue(), this.selfDocProfileId()) !== this.originalSnapshot());

  ngOnInit(): void {
    this.allCountries.set(this.allCountriesInput() ?? []);
    this.setOrganizationData(this.organizationInput() ?? new OrganizationDto());
  }

  protected saveChanges(): void {
    if (!this.hasChanges() || this.isSaving()) return;

    this.isSaving.set(true);

    const manufacturer = this.selfManufacturer();
    const docProfileId = this.selfDocProfileId() === this.noDocProfileId ? null : this.selfDocProfileId();
    const requests = [
      this.userApiService.updateOrganizationInfos(this.toCompanyDto(), true),
      ...(manufacturer && docProfileId !== (manufacturer.defaultDocProfileId ?? null) ? [this.userApiService.createOrUpdateManufacturerProfiles([{ ...manufacturer, defaultDocProfileId: docProfileId }], true)] : []),
    ];

    forkJoin(requests)
      .pipe(
        finalize(() => this.isSaving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.snackbarService.showInfo(this.translocoService.translate('dataManagement.organizationSettings.messages.saveSuccess'), 3000);
          this.loadOrganization();
          this.saved.emit();
        },
        error: () => this.snackbarService.showError('dataManagement.organizationSettings.messages.saveError'),
      });
  }

  protected cancelChanges(): void {
    const original = JSON.parse(this.originalSnapshot() || '{}');
    this.patchForm(original.formValues ?? {});
    this.selfDocProfileId.set(original.selfDocProfileId ?? this.noDocProfileId);
  }

  private loadOrganization(): void {
    this.userApiService
      .getOwnOrganization(true)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => this.setOrganizationData(result),
        error: error => this.snackbarService.showError(getApiErrorMessage(error)),
      });
  }

  private toCompanyDto(): CompanyDto {
    const value = this.organizationForm.getRawValue();
    const organization = this.organization();
    return {
      companyProfileId: organization.id,
      companyName: organization.name,
      address: value.address ?? null,
      postCode: value.postCode ?? null,
      city: value.city ?? null,
      location: value.location ?? null,
    };
  }

  private setOrganizationData(result: OrganizationDto): void {
    this.organization.set(result);
    this.patchForm(result);
    this.selfDocProfileId.set(this.selfManufacturer()?.defaultDocProfileId ?? this.noDocProfileId);
    this.originalSnapshot.set(this.createSnapshot(this.organizationForm.getRawValue(), this.selfDocProfileId()));
  }

  private patchForm(values: Partial<CompanyFormValue>): void {
    this.organizationForm.patchValue({
      address: values.address ?? null,
      postCode: values.postCode ?? null,
      city: values.city ?? null,
      location: values.location ?? null,
    });
  }

  private createSnapshot(formValues: Partial<CompanyFormValue>, selfDocProfileId: string): string {
    return JSON.stringify({ formValues, selfDocProfileId });
  }
}
